import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import type { ResolvedTopicWithEstimate, TopicCompetitorSource, TopicOverviewData } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { useBrandsStore } from "@/stores/brands";
import { cn } from "@/lib/utils";

const PROVENANCE: Record<TopicCompetitorSource, string> = {
  manual: "added manually",
  cohort_migration: "migrated from cohort",
  study_adoption: "from study adoption",
  blind_discovery: "discovered via blind query",
};

const num = (v: number | null | undefined, digits = 0) => (v === null || v === undefined ? "—" : v.toFixed(digits));

interface TopicCompetitorsTabProps {
  topicId: number;
  resolved: ResolvedTopicWithEstimate;
  onMutated: () => Promise<void>;
}

export function TopicCompetitorsTab({ topicId, resolved, onMutated }: TopicCompetitorsTabProps) {
  const brands = useBrandsStore((s) => s.brands);
  const fetchBrands = useBrandsStore((s) => s.fetch);

  const [picker, setPicker] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState<TopicOverviewData | null>(null);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  // Decorative per-competitor stats — a failed fetch just leaves em-dashes.
  useEffect(() => {
    let cancelled = false;
    api.topics.overview(topicId)
      .then((o) => { if (!cancelled) setOverview(o); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [topicId]);

  const trackingEnabled = !!resolved.tracking?.enabled;
  const primary = resolved.primaryBrandId !== null ? brands.find((b) => b.id === resolved.primaryBrandId) : undefined;
  const competitorIds = new Set(resolved.competitors.map((c) => c.id));
  const available = brands.filter((b) => b.id !== resolved.primaryBrandId && !competitorIds.has(b.id));

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onMutated();
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const addExisting = () => {
    const id = parseInt(picker);
    if (!Number.isInteger(id)) return;
    run(async () => { await api.topics.addCompetitor(topicId, id); setPicker(""); });
  };

  const addNew = () => {
    if (!newBrand.trim()) return;
    run(async () => {
      const brand = await api.brands.create(newBrand.trim());
      await api.topics.addCompetitor(topicId, brand.id);
      await fetchBrands();
      setNewBrand("");
    });
  };

  const remove = (brandId: number) => run(async () => { await api.topics.removeCompetitor(topicId, brandId); });

  if (resolved.primaryBrandId === null) {
    return (
      <EmptyState
        icon={Building2}
        title="Set a primary brand"
        description="Competitors are benchmarked against your primary brand. Pick a primary brand for this workspace first."
        action={<Link to="/app/brands"><Button>Go to Brands</Button></Link>}
      />
    );
  }

  return (
    <div className="space-y-5">
      {error && <p className="text-[12.5px] text-red-600">{error}</p>}

      <Card>
        <CardContent className="space-y-1">
          <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 bg-brand-50/40 -mx-6 px-6">
            <Building2 className="w-4 h-4 text-brand-500 shrink-0" />
            <span className="text-[13.5px] font-semibold text-brand-950">{primary?.name ?? "Your brand"}</span>
            <Badge variant="muted">Your brand</Badge>
          </div>
          {resolved.competitors.length === 0 ? (
            <p className="text-[12.5px] text-slate-500 py-3">No competitors yet. Add one below.</p>
          ) : (
            resolved.competitors.map((c) => {
              const fieldLatest = overview?.field?.sentiment_series.find((s) => s.brand_id === c.id)?.latest ?? null;
              const blindEntry = overview?.blind?.leaderboard.entries.find((e) => e.brand_id === c.id) ?? null;
              const shareDelta = blindEntry?.share_delta ?? null;
              return (
              <div key={c.id} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-brand-950 truncate">{c.name}</div>
                  <div className="text-[11.5px] text-slate-400">{PROVENANCE[c.added_from]}</div>
                </div>
                <div className="hidden sm:flex items-center gap-6 shrink-0 tabular-nums">
                  <div className="text-right min-w-[56px]">
                    <div className="text-[14px] font-semibold text-brand-950">{num(fieldLatest, 1)}</div>
                    <div className="text-[10.5px] uppercase tracking-[0.04em] text-slate-400">Sentiment</div>
                  </div>
                  <div className="text-right min-w-[56px]">
                    <div className="text-[14px] font-semibold text-brand-950">{blindEntry ? `${(blindEntry.share * 100).toFixed(0)}%` : "—"}</div>
                    <div className="text-[10.5px] uppercase tracking-[0.04em] text-slate-400">Visibility</div>
                  </div>
                  <div className="text-right min-w-[44px]">
                    {shareDelta !== null && Math.abs(shareDelta) > 0.001 ? (
                      <div className={cn("text-[13px] font-semibold", shareDelta > 0 ? "text-green-600" : "text-red-500")}>
                        {shareDelta > 0 ? "+" : "-"}{Math.abs(shareDelta * 100).toFixed(0)}
                      </div>
                    ) : <div className="text-[13px] text-slate-300">—</div>}
                    <div className="text-[10.5px] uppercase tracking-[0.04em] text-slate-400">WoW</div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-red-500 shrink-0"
                  aria-label={`Remove ${c.name}`}
                  disabled={busy}
                  onClick={() => remove(c.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3">
          <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Add a competitor</span>
          <div className="flex gap-2">
            <NativeSelect value={picker} aria-label="Choose an existing brand to add" onChange={(e) => setPicker(e.target.value)} disabled={available.length === 0}>
              <option value="">{available.length === 0 ? "No more brands to add" : "Choose an existing brand…"}</option>
              {available.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </NativeSelect>
            <Button type="button" variant="secondary" onClick={addExisting} disabled={!picker || busy}>
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Or create a new brand by name…"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNew(); } }}
            />
            <Button type="button" variant="secondary" onClick={addNew} disabled={!newBrand.trim() || busy}>
              <Plus className="w-4 h-4 mr-1" />New brand
            </Button>
          </div>
          {trackingEnabled && (
            <p className="text-[11.5px] text-slate-400">
              Tracking is on — changing the competitor set re-compiles this topic's scheduled runs automatically.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
